<template>
    <div class="ml-page">
        <!-- แถบโหลดด้านบน แสดงตอนยังมีข้อมูลส่วนไหนโหลดไม่เสร็จ -->
        <v-progress-linear
            v-if="anyLoading"
            indeterminate
            color="blue darken-2"
            height="3"
            class="ml-topbar"
        ></v-progress-linear>

        <h1 class="ml-title">Mileage</h1>

        <!-- ===== แถบรอบจ่าย (Paycode) ===== -->
        <section class="ml-paycode" :class="{ past: !isActive }">
            <div class="ml-paycode-main">
                <div class="ml-paycode-name">
                    <span class="ml-paycode-label">{{
                        isActive ? "รอบจ่ายปัจจุบัน" : "รอบจ่ายที่กำลังดู"
                    }}</span>
                    <span class="ml-paycode-code">{{ selectedPaycode || "…" }}</span>
                </div>
                <div class="ml-paycode-meta">
                    <span v-if="isActive" class="ml-chip-active">
                        <span class="ml-dot"></span>รอบปัจจุบัน (active)
                    </span>
                    <span v-else class="ml-chip-past">
                        <v-icon x-small color="#3A4555">mdi-lock-outline</v-icon>
                        รอบที่ปิดแล้ว · ดูอย่างเดียว
                    </span>
                    <span v-if="period.start" class="ml-paycode-sub">
                        ช่วงวันที่ onsite: {{ period.start }} – {{ period.stop }}
                    </span>
                    <span class="ml-paycode-sub2">ข้อมูลไม่รวมเคส return</span>
                </div>
            </div>

            <div class="ml-paycode-side">
                <div v-if="roundOptions.length" class="ml-rounds" role="group" aria-label="เลือกรอบจ่าย">
                    <button
                        v-for="pc in roundOptions"
                        :key="pc"
                        type="button"
                        class="ml-round"
                        :class="{ on: pc === selectedPaycode }"
                        :disabled="chk_data_sum"
                        @click="selectPaycode(pc)"
                    >
                        {{ pc.replace("PAY", "") }}
                    </button>
                </div>
                <v-btn
                    class="ml-btn-export"
                    depressed
                    :loading="chk_export"
                    :disabled="chk_data_sum"
                    @click="export_data()"
                >
                    <v-icon left small>mdi-download</v-icon>
                    Export {{ selectedPaycode || "" }}
                </v-btn>
            </div>
        </section>

        <div v-if="uiPreview" class="ml-preview" role="note">
            <v-icon small color="#92400E">mdi-flask-outline</v-icon>
            โหมดทดสอบ UI (เฉพาะ localhost): API ที่ต่ออยู่ยังไม่รองรับการดูย้อนหลัง
            กดเปลี่ยนรอบได้เพื่อดูหน้าตา แต่ตัวเลขยังเป็นรอบปัจจุบัน
        </div>

        <!-- ===== KPI ===== -->
        <section class="ml-kpis">
            <div class="ml-card ml-kpi">
                <span class="ml-kpi-label">ค่า mileage รวม</span>
                <template v-if="!chk_data_sum">
                    <span class="ml-kpi-value"
                        >{{ data_sum_all.sum_currency }} <small>THB</small></span
                    >
                    <span
                        v-if="compare"
                        class="ml-kpi-note"
                        :class="compare.pct >= 0 ? 'ml-up' : 'ml-down'"
                    >
                        {{ compare.pct >= 0 ? "▲" : "▼" }}
                        {{ Math.abs(compare.pct).toFixed(1) }}% จาก
                        {{ compare.prevLabel }}
                        <span class="ml-muted"
                            >({{ formatK(compare.prev) }})</span
                        >
                    </span>
                </template>
                <v-skeleton-loader v-else type="heading, text"></v-skeleton-loader>
            </div>

            <div class="ml-card ml-kpi">
                <span class="ml-kpi-label">เคสเฉลี่ยต่อ CE</span>
                <template v-if="!chk_data_sum">
                    <span class="ml-kpi-value"
                        >{{ data_sum_all.vol_job }} <small>เคส</small></span
                    >
                    <span class="ml-kpi-note">CE ทั้งหมด {{ totals.ce }} คน</span>
                </template>
                <v-skeleton-loader v-else type="heading, text"></v-skeleton-loader>
            </div>

            <div class="ml-card ml-kpi">
                <span class="ml-kpi-label">ระยะทางเฉลี่ยต่อเคส</span>
                <template v-if="!chk_data_sum">
                    <span class="ml-kpi-value"
                        >{{ data_sum_all.distance }} <small>km</small></span
                    >
                    <span class="ml-kpi-note"
                        >{{ data_sum_all.cost }} THB ต่อเคส</span
                    >
                </template>
                <v-skeleton-loader v-else type="heading, text"></v-skeleton-loader>
            </div>

            <div class="ml-card ml-kpi" :class="{ 'ml-kpi-warn': totals.open > 0 }">
                <span class="ml-kpi-label">{{ legacyStatus ? "รออนุมัติ (ขั้นของคุณ)" : "CE ที่ยังค้าง" }}</span>
                <template v-if="!chk_data_sum">
                    <span class="ml-kpi-value"
                        >{{ totals.open }} <small>{{ legacyStatus ? "รายการ" : "คน" }}</small></span
                    >
                    <span v-if="legacyStatus" class="ml-kpi-note"
                        >ไม่ผ่าน {{ totals.reject }}</span
                    >
                    <span v-else class="ml-kpi-note"
                        >ยังไม่ส่ง {{ totals.notSent }} · รออนุมัติ
                        {{ totals.waiting }} · ไม่ผ่าน {{ totals.reject }}</span
                    >
                </template>
                <v-skeleton-loader v-else type="heading, text"></v-skeleton-loader>
            </div>
        </section>

        <!-- ===== กราฟรายรอบ + Top 5 ===== -->
        <section class="ml-row">
            <div class="ml-card ml-chart">
                <div class="ml-chart-head">
                    <div class="ml-col">
                        <h2>ค่า mileage รายรอบ</h2>
                        <span class="ml-hint">{{
                            roundList.length
                                ? "แยกสีตามโซน · ชี้ที่แท่งเพื่อดูรายละเอียด · กดแท่งเพื่อเปลี่ยนรอบที่ดู"
                                : "แยกสีตามโซน · ชี้ที่แท่งเพื่อดูรายละเอียด"
                        }}</span>
                    </div>
                    <div v-if="chartFocus" class="ml-chart-hero" aria-live="polite">
                        <span class="ml-chart-hero-label">{{ chartFocus.label }}</span>
                        <span class="ml-chart-hero-val"
                            >{{ formatMoney(chartFocus.total) }} <small>THB</small></span
                        >
                        <span
                            v-if="chartFocus.pct !== null"
                            class="ml-delta"
                            :class="chartFocus.pct >= 0 ? 'up' : 'down'"
                            >{{ chartFocus.pct >= 0 ? "▲" : "▼" }}
                            {{ Math.abs(chartFocus.pct).toFixed(1) }}% จาก
                            {{ chartFocus.prevLabel }}</span
                        >
                    </div>
                </div>

                <div v-if="chk_data_graph" class="ml-chart-loading">
                    <v-progress-circular
                        indeterminate
                        color="blue darken-2"
                    ></v-progress-circular>
                    <span>กำลังคำนวณกราฟ (ใช้เวลาสักครู่)</span>
                </div>

                <template v-else-if="chart">
                    <div class="ml-plot" @mouseleave="hoverIdx = -1">
                        <div class="ml-yaxis" aria-hidden="true">
                            <span
                                v-for="t in chart.ticks"
                                :key="'y' + t.value"
                                :style="{ bottom: t.pct + '%' }"
                                >{{ formatShort(t.value) }}</span
                            >
                        </div>
                        <div class="ml-plot-area">
                            <div
                                v-for="t in chart.ticks"
                                :key="'g' + t.value"
                                class="ml-grid"
                                :class="{ base: t.value === 0 }"
                                :style="{ bottom: t.pct + '%' }"
                            ></div>
                            <div class="ml-avg" :style="{ bottom: chart.avgPct + '%' }"></div>
                            <div class="ml-cols">
                                <button
                                    v-for="(c, i) in chart.cols"
                                    :key="c.label"
                                    type="button"
                                    class="ml-colb"
                                    :class="{
                                        on: c.label === selectedPaycode,
                                        hover: hoverIdx === i,
                                        clickable: c.selectable && c.label !== selectedPaycode,
                                    }"
                                    :aria-label="c.label + ' รวม ' + formatMoney(c.total) + ' บาท'"
                                    @mouseenter="hoverIdx = i"
                                    @focus="hoverIdx = i"
                                    @blur="hoverIdx = -1"
                                    @click="c.selectable && selectPaycode(c.label)"
                                >
                                    <span
                                        class="ml-colb-total"
                                        :style="{ bottom: 'calc(' + c.pct + '% + 6px)' }"
                                        >{{ formatShort(c.total) }}</span
                                    >
                                    <span class="ml-colb-stack" :style="{ height: c.pct + '%' }">
                                        <span
                                            v-for="seg in c.segs"
                                            :key="seg.key"
                                            class="ml-seg"
                                            :class="{ faded: hoverZone && hoverZone !== seg.key }"
                                            :style="{ flexGrow: seg.value, background: seg.color }"
                                        ></span>
                                    </span>
                                </button>
                            </div>

                            <!-- tooltip -->
                            <div
                                v-if="hoverCol"
                                class="ml-tip"
                                :class="{ flip: hoverIdx >= chart.cols.length - 2 }"
                                :style="{ left: ((hoverIdx + 0.5) / chart.cols.length) * 100 + '%' }"
                                role="tooltip"
                            >
                                <div class="ml-tip-head">
                                    <b>{{ hoverCol.label }}</b>
                                    <span
                                        v-if="hoverCol.pct !== null"
                                        class="ml-delta sm"
                                        :class="hoverCol.pct >= 0 ? 'up' : 'down'"
                                        >{{ hoverCol.pct >= 0 ? "▲" : "▼" }}
                                        {{ Math.abs(hoverCol.pct).toFixed(1) }}%</span
                                    >
                                </div>
                                <div class="ml-tip-total">
                                    {{ formatMoney(hoverCol.total) }} <small>THB</small>
                                </div>
                                <ul class="ml-tip-list">
                                    <li v-for="seg in hoverCol.segsDesc" :key="seg.key">
                                        <i :style="{ background: seg.color }"></i>
                                        <span class="n">{{ seg.name }}</span>
                                        <span class="v">{{ formatShort(seg.value) }}</span>
                                        <span class="p">{{ seg.share.toFixed(0) }}%</span>
                                    </li>
                                </ul>
                                <div v-if="hoverCol.selectable && hoverCol.label !== selectedPaycode" class="ml-tip-foot">
                                    กดเพื่อดูรอบนี้ทั้งหน้า
                                </div>
                                <div v-else-if="hoverCol.label === selectedPaycode" class="ml-tip-foot">
                                    รอบที่กำลังดูอยู่
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="ml-xaxis">
                        <span
                            v-for="(c, i) in chart.cols"
                            :key="'x' + c.label"
                            :class="{ on: c.label === selectedPaycode, hover: hoverIdx === i }"
                        >
                            {{ c.label.replace("PAY", "") }}
                            <em v-if="c.label === selectedPaycode">กำลังดู</em>
                        </span>
                    </div>

                    <div class="ml-zlegend" @mouseleave="hoverZone = ''">
                        <button
                            v-for="z in chart.zones"
                            :key="z.key"
                            type="button"
                            :class="{ faded: hoverZone && hoverZone !== z.key }"
                            @mouseenter="hoverZone = z.key"
                            @focus="hoverZone = z.key"
                            @blur="hoverZone = ''"
                        >
                            <i :style="{ background: z.color }"></i>{{ z.name }}
                        </button>
                        <span class="ml-zlegend-avg"
                            ><b></b>เฉลี่ย {{ chart.cols.length }} รอบ
                            {{ formatShort(chart.avg) }}</span
                        >
                    </div>
                </template>
                <div v-else class="ml-chart-loading">ไม่มีข้อมูลกราฟ</div>
            </div>

            <div class="ml-side">
                <div class="ml-card">
                    <div class="ml-card-head">
                        <h2 class="sm">Top 5 จำนวนเคส</h2>
                        <v-btn
                            text
                            small
                            color="blue darken-2"
                            :loading="chk_view_top1"
                            @click="get_view_top('volume')"
                            >ดูทั้งหมด</v-btn
                        >
                    </div>
                    <v-skeleton-loader v-if="loading_vol" type="text@5"></v-skeleton-loader>
                    <div v-else class="ml-top">
                        <template v-for="(r, i) in topVolume">
                            <span :key="'n' + i" class="ml-top-name"
                                >{{ i + 1 }}. {{ r.name }}</span
                            >
                            <span :key="'v' + i" class="ml-top-val">{{ r.label }}</span>
                            <div :key="'b' + i" class="ml-bar">
                                <div class="ml-bar-fill" :style="{ width: r.pct + '%' }"></div>
                            </div>
                        </template>
                    </div>
                </div>

                <div class="ml-card">
                    <div class="ml-card-head">
                        <h2 class="sm">Top 5 ระยะทาง (km)</h2>
                        <v-btn
                            text
                            small
                            color="blue darken-2"
                            :loading="chk_view_top2"
                            @click="get_view_top('distance')"
                            >ดูทั้งหมด</v-btn
                        >
                    </div>
                    <v-skeleton-loader v-if="loading_km" type="text@5"></v-skeleton-loader>
                    <div v-else class="ml-top">
                        <template v-for="(r, i) in topKm">
                            <span :key="'n' + i" class="ml-top-name"
                                >{{ i + 1 }}. {{ r.name }}</span
                            >
                            <span :key="'v' + i" class="ml-top-val">{{ r.label }}</span>
                            <div :key="'b' + i" class="ml-bar">
                                <div
                                    class="ml-bar-fill orange"
                                    :style="{ width: r.pct + '%' }"
                                ></div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===== ตารางรายโซน ===== -->
        <section class="ml-card">
            <div class="ml-card-head">
                <h2>เทียบรายโซน (TL)</h2>
                <span class="ml-hint"
                    >กดหัวคอลัมน์เพื่อเรียง ·
                    <b class="ml-warn-text">สีส้ม</b> = THB/เคส สูงกว่าค่าเฉลี่ยเกิน
                    10% · จุดส้มบนปุ่ม = CE ที่ยังค้าง</span
                >
            </div>

            <v-skeleton-loader v-if="chk_data_sum" type="table-tbody"></v-skeleton-loader>

            <div v-else class="ml-table-wrap">
                <table class="ml-table">
                    <thead>
                        <tr>
                            <th class="l">
                                <button type="button" @click="sortBy('tl_name')">
                                    TL / โซน {{ sortIcon("tl_name") }}
                                </button>
                            </th>
                            <th>
                                <button type="button" @click="sortBy('sum')">
                                    ค่า mileage (THB) {{ sortIcon("sum") }}
                                </button>
                            </th>
                            <th>
                                <button type="button" @click="sortBy('vol')">
                                    เคส / CE {{ sortIcon("vol") }}
                                </button>
                            </th>
                            <th>
                                <button type="button" @click="sortBy('dist')">
                                    km / เคส {{ sortIcon("dist") }}
                                </button>
                            </th>
                            <th>
                                <button type="button" @click="sortBy('cost')">
                                    THB / เคส {{ sortIcon("cost") }}
                                </button>
                            </th>
                            <th class="l">สถานะเบิก (จำนวน CE)</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="z in zones" :key="z.tl_id">
                            <td class="l b">{{ z.tl_name }}</td>
                            <td>
                                <div class="ml-cell-bar">
                                    <div class="ml-bar w120">
                                        <div
                                            class="ml-bar-fill"
                                            :style="{ width: z.sumPct + '%' }"
                                        ></div>
                                    </div>
                                    <span class="b">{{ z.sum_currency }}</span>
                                </div>
                            </td>
                            <td>{{ z.vol_job }}</td>
                            <td>{{ z.distance }}</td>
                            <td>
                                <span :class="{ 'ml-hl': z.costHigh }">{{ z.cost }}</span>
                            </td>
                            <td v-if="z.st.legacy" class="l">
                                <!-- API เก่า: ยังไม่มีตัวนับแยกขั้น ใช้ตัวเลขเดิม -->
                                <span class="ml-pill" :class="z.st.approved === z.total ? 'ok' : ''"
                                    >อนุมัติ {{ z.st.approved }}/{{ z.total }}</span
                                >
                                <span v-if="z.st.pending" class="ml-pill wait"
                                    >รออนุมัติ {{ z.st.pending }}</span
                                >
                                <span v-if="z.st.reject" class="ml-pill bad"
                                    >ไม่ผ่าน {{ z.st.reject }}</span
                                >
                            </td>
                            <td v-else class="l">
                                <span
                                    class="ml-pill"
                                    :class="z.st.done === z.total ? 'ok' : ''"
                                    >อนุมัติครบ {{ z.st.done }}/{{ z.total }}</span
                                >
                                <span v-if="z.st.notSent" class="ml-pill warn"
                                    >ยังไม่ส่ง {{ z.st.notSent }}</span
                                >
                                <span v-if="z.st.tl" class="ml-pill wait"
                                    >รอ TL {{ z.st.tl }}</span
                                >
                                <span v-if="z.st.mgr" class="ml-pill wait"
                                    >รอ MGR {{ z.st.mgr }}</span
                                >
                                <span v-if="z.st.md" class="ml-pill wait"
                                    >รอ MD {{ z.st.md }}</span
                                >
                                <span v-if="z.st.reject" class="ml-pill bad"
                                    >ไม่ผ่าน {{ z.st.reject }}</span
                                >
                                <span
                                    v-if="z.st.dup"
                                    class="ml-pill bad"
                                    title="มี CE ที่มีรายการเบิกมากกว่า 1 รายการในรอบเดียว"
                                    >⚠ ข้อมูลซ้ำ {{ z.st.dup }}</span
                                >
                            </td>
                            <td>
                                <router-link
                                    :to="zoneLink(z)"
                                    class="ml-zone-btn"
                                    :class="{ past: !isActive }"
                                    :aria-label="'เปิดรายละเอียดโซน ' + z.tl_name + ' รอบ ' + selectedPaycode"
                                >
                                    <span class="ml-zone-btn-main">
                                        <v-icon small color="#BFD0FF">{{
                                            isActive ? "mdi-account-group-outline" : "mdi-history"
                                        }}</v-icon>
                                        {{ z.total }} CE
                                    </span>
                                    <span class="ml-zone-btn-go">
                                        <v-icon small color="#1E3A8A">mdi-arrow-right</v-icon>
                                    </span>
                                    <span v-if="z.open > 0" class="ml-zone-btn-dot">{{ z.open }}</span>
                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>


        <!-- Dialog ดูทั้งหมด (เหมือนเดิม) -->
        <v-dialog
            v-model="dialogViewTop"
            fullscreen
            hide-overlay
            transition="dialog-bottom-transition"
        >
            <v-card>
                <v-toolbar dark color="primary">
                    <v-btn icon dark @click="dialogViewTop = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title
                        >View All Performance w/o return ( by
                        {{ form_top_view.type }} ) · {{ selectedPaycode }}</v-toolbar-title
                    >
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-card-text>
                    <ViewTop :data_top="data_top" :form_top_view="form_top_view" />
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import axios from "axios"
import { mapState, mapMutations } from "vuex"

import ViewTop from "./components/ViewTopOnsite"

let url = `${process.env.VUE_APP_ROOT_API}/api/`

// "1,369,170.10" -> 1369170.1
function num(v) {
    if (v === null || v === undefined || v === "") return 0
    const n = parseFloat(String(v).replace(/,/g, ""))
    return isNaN(n) ? 0 : n
}

const IS_DEV = process.env.NODE_ENV !== "production"

// สีโซน (ลำดับตายตัว ผ่านการเช็คตาบอดสีแล้ว) + เทาสำหรับ "อื่นๆ"
const ZONE_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#6250d6"]
const ZONE_OTHER = "#a7b0bd"

// แกน Y: ปัดเป็นเลขกลมๆ (1, 2, 2.5, 5 x 10^n) ประมาณ 4 ช่อง
function niceTicks(max) {
    const raw = max / 4
    const mag = Math.pow(10, Math.floor(Math.log10(raw)))
    const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)
    const ticks = []
    for (let v = 0; v <= max + step * 0.0001 || ticks.length < 2; v += step) ticks.push(v)
    if (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step)
    return ticks
}

export default {
    components: { ViewTop },
    computed: {
        ...mapState(["isLoading"]),
        anyLoading() {
            return this.chk_data_graph || this.chk_data_sum || this.loading_vol || this.loading_km
        },
        // รอบปัจจุบัน = รอบแรกของ paycode_options (API เรียงใหม่ -> เก่า)
        // ถ้า API ยังไม่มี paycode_options (backend เก่า) ถือว่าเป็นรอบปัจจุบันเสมอ
        currentPaycode() {
            return this.activePaycode || this.roundList[0] || ""
        },
        isActive() {
            return !this.selectedPaycode || !this.currentPaycode || this.selectedPaycode === this.currentPaycode
        },
        // รอบที่เลือกได้ (จาก API) เรียงเก่า -> ใหม่ ให้ตรงกับกราฟ
        // รอบที่เลือกได้ เรียงใหม่ -> เก่า
        // - API ใหม่ส่ง paycode_options มา -> ใช้ตามนั้น
        // - API เก่า (prod ตอนนี้) -> ไม่มีปุ่มเลือกรอบ (กันโชว์ตัวเลขผิดรอบ)
        // - ยกเว้นตอนรันบน localhost (npm run serve) เปิด "โหมดทดสอบ UI" ให้กดดูหน้าตาได้
        roundList() {
            if (this.paycodeOptions.length) return this.paycodeOptions
            if (!IS_DEV) return []
            const labels = (this.data_graph && this.data_graph.labels) || []
            return labels.slice(-4).reverse()
        },
        uiPreview() {
            return !this.paycodeOptions.length && this.roundList.length > 0
        },
        // ปุ่มรอบในแถบ paycode เรียงเก่า -> ใหม่ ให้ตรงกับกราฟ
        roundOptions() {
            return this.roundList.slice().reverse()
        },
        graphAll() {
            const g = this.data_graph
            if (!g || !g.labels || !g.datasets) return null
            const all = g.datasets.find((d) => d.label === "All")
            if (!all) return null
            return { labels: g.labels, data: all.data.map(num) }
        },
        // กราฟแท่งซ้อนแยกตามโซน (จาก mileage-cost-graph ซึ่งมีบน prod อยู่แล้ว)
        chart() {
            const g = this.data_graph
            if (!g || !g.labels || !g.datasets || !g.labels.length) return null
            const n = g.labels.length
            const series = g.datasets
                .filter((d) => d.label !== "All")
                .map((d) => ({ name: d.label, data: d.data.map(num) }))
                .map((d) => ({ ...d, sum: d.data.reduce((a, b) => a + b, 0) }))
                .filter((d) => d.sum > 0)
                .sort((a, b) => b.sum - a.sum)
            // เกิน 6 โซน: เก็บ 5 อันดับแรก ที่เหลือรวมเป็น "อื่นๆ"
            let zones = series
            if (series.length > ZONE_COLORS.length) {
                const keep = series.slice(0, ZONE_COLORS.length - 1)
                const rest = series.slice(ZONE_COLORS.length - 1)
                const other = { name: "อื่นๆ (" + rest.length + " โซน)", data: [], other: true }
                for (let i = 0; i < n; i++) other.data.push(rest.reduce((a, d) => a + d.data[i], 0))
                zones = keep.concat([other])
            }
            zones = zones.map((z, i) => ({
                key: z.name,
                name: z.name,
                data: z.data,
                color: z.other ? ZONE_OTHER : ZONE_COLORS[i],
            }))
            const totals = g.labels.map((_, i) => zones.reduce((a, z) => a + z.data[i], 0))
            const ticks = niceTicks(Math.max(1, ...totals))
            const top = ticks[ticks.length - 1]
            const avg = totals.reduce((a, b) => a + b, 0) / n
            const cols = g.labels.map((label, i) => {
                const total = totals[i]
                const segs = zones
                    .map((z) => ({
                        key: z.key,
                        name: z.name,
                        color: z.color,
                        value: z.data[i],
                        share: total > 0 ? (z.data[i] / total) * 100 : 0,
                    }))
                    .filter((s) => s.value > 0)
                return {
                    label,
                    total,
                    pct: (total / top) * 100,
                    delta: i > 0 ? totals[i - 1] : null,
                    prevLabel: i > 0 ? g.labels[i - 1] : "",
                    pct2: i > 0 && totals[i - 1] > 0 ? ((total - totals[i - 1]) / totals[i - 1]) * 100 : null,
                    segs,
                    segsDesc: segs.slice().sort((a, b) => b.value - a.value),
                    selectable: this.roundList.indexOf(label) !== -1,
                }
            })
            return {
                zones,
                cols,
                avg,
                avgPct: (avg / top) * 100,
                ticks: ticks.map((v) => ({ value: v, pct: (v / top) * 100 })),
            }
        },
        hoverCol() {
            if (!this.chart || this.hoverIdx < 0) return null
            const c = this.chart.cols[this.hoverIdx]
            return c ? { ...c, pct: c.pct2 } : null
        },
        // ตัวเลขใหญ่มุมขวาบนของกราฟ: แท่งที่ชี้อยู่ ถ้าไม่ได้ชี้ = รอบที่กำลังดู
        chartFocus() {
            if (!this.chart) return null
            const c =
                this.hoverCol ||
                this.chart.cols.find((x) => x.label === this.selectedPaycode) ||
                this.chart.cols[this.chart.cols.length - 1]
            return { label: c.label, total: c.total, pct: c.pct2, prevLabel: c.prevLabel }
        },
        // เทียบรอบที่เลือกกับรอบก่อนหน้า จากกราฟ
        compare() {
            const g = this.graphAll
            if (!g) return null
            const i = g.labels.indexOf(this.selectedPaycode)
            if (i < 1) return null
            const cur = g.data[i]
            const prev = g.data[i - 1]
            return {
                prev,
                prevLabel: g.labels[i - 1],
                pct: prev > 0 ? ((cur - prev) / prev) * 100 : 0,
            }
        },
        zones() {
            const list = (this.data_sum && this.data_sum.zone) || []
            const avgCost = num(this.data_sum_all && this.data_sum_all.cost)
            const maxSum = Math.max(1, ...list.map((r) => num(r.sum_currency)))
            const rows = list.map((r) => {
                const total = num(r.total)
                if (r.st_not_sent === undefined) {
                    // API เก่า (ยังไม่มี st_*)
                    const st = {
                        legacy: true,
                        approved: num(r.approved),
                        pending: num(r.pending),
                        reject: num(r.reject),
                        notSent: 0,
                        tl: 0,
                        mgr: 0,
                        md: num(r.pending),
                    }
                    return {
                        ...r,
                        total,
                        st,
                        open: st.pending,
                        sum: num(r.sum_currency),
                        vol: num(r.vol_job),
                        dist: num(r.distance),
                        costN: num(r.cost),
                        sumPct: (num(r.sum_currency) / maxSum) * 100,
                        costHigh: avgCost > 0 && num(r.cost) > avgCost * 1.1,
                    }
                }
                const st = {
                    notSent: num(r.st_not_sent),
                    tl: num(r.st_wait_tl),
                    mgr: num(r.st_wait_mgr),
                    md: num(r.st_wait_md),
                    done: num(r.st_done),
                    reject: num(r.st_reject),
                }
                const sentPeople = total - st.notSent
                st.dup = Math.max(0, num(r.st_rows) - sentPeople)
                return {
                    ...r,
                    total,
                    st,
                    open: st.notSent + st.tl + st.mgr + st.md,
                    sum: num(r.sum_currency),
                    vol: num(r.vol_job),
                    dist: num(r.distance),
                    costN: num(r.cost),
                    sumPct: (num(r.sum_currency) / maxSum) * 100,
                    costHigh: avgCost > 0 && num(r.cost) > avgCost * 1.1,
                }
            })
            const key = this.sortKey === "cost" ? "costN" : this.sortKey
            const dir = this.sortDesc ? -1 : 1
            return rows.sort((a, b) => {
                if (key === "tl_name") return dir * String(a.tl_name).localeCompare(String(b.tl_name))
                return dir * (a[key] - b[key])
            })
        },
        legacyStatus() {
            return this.zones.length > 0 && this.zones[0].st.legacy === true
        },
        totals() {
            return this.zones.reduce(
                (a, z) => ({
                    ce: a.ce + z.total,
                    open: a.open + z.open,
                    notSent: a.notSent + z.st.notSent,
                    waiting: a.waiting + z.st.tl + z.st.mgr + z.st.md,
                    reject: a.reject + z.st.reject,
                }),
                { ce: 0, open: 0, notSent: 0, waiting: 0, reject: 0 }
            )
        },
        topVolume() {
            const list = this.data_top_mileage || []
            const max = Math.max(1, ...list.map((r) => num(r.volume_job)))
            return list.map((r) => ({
                name: r.ce_name,
                label: r.volume_job,
                pct: (num(r.volume_job) / max) * 100,
            }))
        },
        topKm() {
            const list = this.data_top_km || []
            const max = Math.max(1, ...list.map((r) => num(r.sum_km)))
            return list.map((r) => ({
                name: r.ce_name,
                label: r.sum_km,
                pct: (num(r.sum_km) / max) * 100,
            }))
        },
    },
    beforeMount() {
        this.tk = {
            headers: { Authorization: "Bearer " + localStorage.token },
        }
        this.role_name = localStorage.role_name
    },
    mounted() {
        this.chk_url()
    },
    methods: {
        ...mapMutations(["mutateIsLoading"]),
        async chk_url() {
            let send = {
                url: this.$route.path,
            }
            await axios
                .post(url + "check-auth", send, this.tk)
                .then((response) => {
                    if (response.data.status == "error") {
                        this.$root.$router.push({
                            path: "/404",
                        })
                    } else {
                        if (this.role_name === "TL") {
                            this.mileage_tl()
                        } else {
                            this.get_graph()
                            this.load_round(this.$route.query.paycode || "")
                        }
                    }
                })
                .catch((error) => {
                    console.log(error)
                })
        },
        // กราฟรายรอบ (6 รอบ) โหลดครั้งเดียว ไม่ขึ้นกับรอบที่เลือก
        get_graph() {
            this.chk_data_graph = true
            axios
                .get(url + "mileage-cost-graph")
                .then((response) => {
                    this.data_graph = response.data
                })
                .catch((error) => console.log(error))
                .finally(() => {
                    this.chk_data_graph = false
                })
        },
        // ข้อมูลของรอบที่เลือก ("" = รอบปัจจุบัน) ยิงพร้อมกัน แต่ละส่วนขึ้นทันทีที่มาถึง
        async load_round(paycode) {
            const q = paycode ? "?paycode=" + encodeURIComponent(paycode) : ""
            this.chk_data_sum = true
            this.loading_vol = true
            this.loading_km = true

            const reqTopVolume = axios
                .get(url + "mileage-ce-graph" + q)
                .then((response) => {
                    this.data_top_mileage = response.data
                })
                .finally(() => {
                    this.loading_vol = false
                })
            const reqTopKm = axios
                .get(url + "mileage-miles-graph" + q)
                .then((response) => {
                    this.data_top_km = response.data
                })
                .finally(() => {
                    this.loading_km = false
                })
            const reqSummary = axios
                .get(url + "mileage-approve-summary" + q, this.tk)
                .then((response) => {
                    const d = response.data
                    this.data_sum = d
                    this.data_sum_all = d.all
                    // โหมดทดสอบ UI: API เก่าไม่สนใจ ?paycode= เลยคงรอบที่กดไว้ให้ดูหน้าตาได้
                    this.selectedPaycode = this.uiPreview && paycode ? paycode : d.paycode
                    if (d.is_active) this.activePaycode = d.paycode
                    if (Array.isArray(d.paycode_options)) this.paycodeOptions = d.paycode_options
                    if (d.period) {
                        this.period.start = this.formatDate(d.period.start)
                        this.period.stop = this.formatDate(d.period.stop)
                    } else {
                        this.load_period(d.paycode) // API เก่ายังไม่ส่ง period มา
                    }
                })
                .finally(() => {
                    this.chk_data_sum = false
                })

            const results = await Promise.allSettled([reqTopVolume, reqTopKm, reqSummary])
            results.forEach((r) => {
                if (r.status === "rejected") console.log(r.reason)
            })
        },
        async load_period(paycode) {
            this.period = { start: "", stop: "" }
            if (!paycode) return
            try {
                const res = await axios.get(url + "expense-period/" + paycode, this.tk)
                const p = res.data && res.data[0]
                if (p) {
                    this.period.start = this.formatDate(p.period_start)
                    this.period.stop = this.formatDate(p.period_stop)
                }
            } catch (e) {
                console.log(e)
            }
        },
        formatShort(v) {
            const n = num(v)
            if (n >= 1000000) return parseFloat((n / 1000000).toFixed(2)) + "M"
            if (n >= 1000) return Math.round(n / 1000).toLocaleString("en-US") + "k"
            return Math.round(n).toString()
        },
        selectPaycode(pc) {
            if (!pc || pc === this.selectedPaycode || this.chk_data_sum) return
            if (this.roundList.indexOf(pc) === -1) return
            this.selectedPaycode = pc
            this.load_round(pc === this.currentPaycode ? "" : pc)
            // จำรอบไว้ใน URL กดกลับจากหน้าโซนแล้วยังอยู่รอบเดิม
            const query = pc === this.currentPaycode ? {} : { paycode: pc }
            if ((this.$route.query.paycode || "") !== (query.paycode || "")) {
                this.$router.replace({ path: this.$route.path, query }).catch(() => {})
            }
        },
        // ปุ่มโซน: เข้าหน้ารายละเอียดโซนเหมือนเดิม ถ้าดูรอบเก่าส่ง ?paycode= ไปด้วย
        zoneLink(z) {
            const link = { path: "/Expense/Mileage/" + z.tl_id + "/all" }
            if (!this.isActive && this.selectedPaycode) {
                link.query = { paycode: this.selectedPaycode }
            }
            return link
        },
        formatDate(v) {
            if (!v) return ""
            const d = new Date(String(v).replace(" ", "T"))
            if (isNaN(d)) return String(v).substring(0, 10)
            return d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric" })
        },
        formatMoney(v) {
            return num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },
        formatK(v) {
            return Math.round(num(v) / 1000).toLocaleString("en-US") + "k"
        },
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortDesc = !this.sortDesc
            } else {
                this.sortKey = key
                this.sortDesc = key !== "tl_name"
            }
        },
        sortIcon(key) {
            if (this.sortKey !== key) return ""
            return this.sortDesc ? "▾" : "▴"
        },
        mileage_tl() {
            this.$root.$router.push({
                path: "/Expense/Mileage/Tl/all",
            })
        },
        async export_data() {
            this.chk_export = true
            const q = this.isActive ? "" : "?paycode=" + encodeURIComponent(this.selectedPaycode)
            await axios({
                url: url + "mileage-export" + q,
                method: "GET",
                responseType: "blob",
            })
                .then((response) => {
                    let fileURL = window.URL.createObjectURL(new Blob([response.data]))
                    let fileLink = document.createElement("a")
                    fileLink.href = fileURL
                    fileLink.setAttribute("download", "mileage_" + (this.selectedPaycode || "export") + ".xlsx")
                    document.body.appendChild(fileLink)
                    fileLink.click()
                })
                .catch((error) => {
                    console.log(error)
                })
                .finally(() => {
                    this.chk_export = false
                })
        },
        async get_view_top(ty) {
            ty == "volume" ? (this.chk_view_top1 = true) : (this.chk_view_top2 = true)
            this.form_top_view.type = ty
            const body = { ...this.form_top_view }
            if (!this.isActive) body.paycode = this.selectedPaycode
            await axios
                .post(url + "mileage-graph-all", body, {})
                .then((response) => {
                    this.data_top = response.data
                    this.dialogViewTop = true
                })
                .catch((error) => {
                    console.log(error)
                })
                .finally(() => {
                    this.chk_view_top1 = false
                    this.chk_view_top2 = false
                })
        },
    },
    data: () => ({
        chk_export: false,
        chk_data_graph: false,
        chk_data_sum: false,
        loading_vol: false,
        loading_km: false,
        data_graph: {},
        data_top_mileage: [],
        data_top_km: [],
        data_sum: {},
        data_sum_all: {},
        period: { start: "", stop: "" },
        selectedPaycode: "",
        activePaycode: "",
        paycodeOptions: [],
        hoverIdx: -1,
        hoverZone: "",
        role_name: null,
        sortKey: "sum",
        sortDesc: true,
        chk_view_top1: false,
        chk_view_top2: false,
        data_top: [],
        dialogViewTop: false,
        form_top_view: {
            type: "",
            tl_id: "",
        },
    }),
}
</script>
<style scoped>
.ml-page {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 20px;
    color: #18212f;
}
.ml-topbar {
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
}
.ml-title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    color: #3a4555;
}
.ml-col {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.ml-muted {
    color: #5b6575;
}

/* Paycode */
.ml-paycode {
    background: #1e3a8a;
    color: #fff;
    border-radius: 14px;
    padding: 20px 24px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    transition: background 0.2s;
}
.ml-paycode.past {
    background: #334155;
}
.ml-paycode-main {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 20px;
}
.ml-paycode-name {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.ml-paycode-label {
    font-size: 13px;
    color: #bfd0ff;
}
.ml-paycode-code {
    font-size: 40px;
    font-weight: 700;
    letter-spacing: 1px;
    line-height: 1.1;
}
.ml-paycode-meta {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.ml-chip-active,
.ml-chip-past {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    font-size: 13px;
    font-weight: 600;
    border-radius: 999px;
    padding: 3px 10px;
}
.ml-chip-active {
    color: #14532d;
    background: #bbf7d0;
}
.ml-chip-past {
    color: #3a4555;
    background: #e2e8f0;
}
.ml-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #15803d;
}
.ml-paycode-sub {
    font-size: 14px;
    color: #dce5ff;
}
.ml-paycode-sub2 {
    font-size: 13px;
    color: #bfd0ff;
}
.ml-paycode-side {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
}
.ml-rounds {
    display: flex;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    padding: 4px;
    gap: 4px;
}
.ml-round {
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    color: #dce5ff;
    background: transparent;
    border: 0;
    border-radius: 7px;
    padding: 0 12px;
    min-height: 36px;
    cursor: pointer;
}
.ml-round:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
}
.ml-round.on {
    background: #fff;
    color: #1e3a8a;
}
.ml-round:disabled {
    cursor: wait;
}
.ml-btn-export {
    background: #fde68a !important;
    color: #1e3a8a !important;
    font-weight: 600;
    min-height: 44px;
    text-transform: none;
    letter-spacing: 0.2px;
}

/* Cards */
.ml-card {
    background: #fff;
    border: 1px solid #e3e7ed;
    border-radius: 12px;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.ml-card-head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}
.ml-card-head h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}
.ml-card-head h2.sm {
    font-size: 16px;
}
.ml-hint {
    font-size: 13px;
    color: #5b6575;
    font-weight: 400;
}
.ml-warn-text {
    color: #9a3412;
}

/* KPI */
.ml-kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}
.ml-kpi {
    gap: 6px;
}
.ml-kpi-label {
    font-size: 14px;
    color: #5b6575;
}
.ml-kpi-value {
    /* จอแคบตัวเลขเล็กลงเอง ไม่ให้หน่วย THB ตกบรรทัด */
    font-size: clamp(22px, 2.3vw, 30px);
    white-space: nowrap;
    font-weight: 700;
    line-height: 1.2;
}
.ml-kpi-value small {
    font-size: 16px;
    font-weight: 500;
    color: #5b6575;
}
.ml-kpi-note {
    font-size: 13px;
    color: #5b6575;
}
.ml-up {
    color: #9a3412;
    font-weight: 600;
}
.ml-down {
    color: #166534;
    font-weight: 600;
}
.ml-kpi-warn {
    background: #fff7ed;
    border-color: #fed7aa;
}
.ml-kpi-warn .ml-kpi-label,
.ml-kpi-warn .ml-kpi-note {
    color: #9a3412;
}
.ml-kpi-warn .ml-kpi-value,
.ml-kpi-warn .ml-kpi-value small {
    color: #7c2d12;
}

/* Chart + tops */
.ml-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}
.ml-chart {
    flex: 999 1 560px;
    min-width: 0;
}
.ml-side {
    flex: 1 1 300px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.ml-chart-loading {
    height: 260px;
    border-radius: 8px;
    background: #f7f9fc;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #5b6575;
    font-size: 14px;
}
.ml-preview {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: -8px;
    padding: 8px 14px;
    border-radius: 10px;
    background: #fef3c7;
    border: 1px dashed #f59e0b;
    color: #92400e;
    font-size: 13px;
}
/* ----- กราฟแท่งซ้อนรายรอบ ----- */
.ml-chart-head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
}
.ml-chart-head h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}
.ml-chart-hero {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    min-width: 180px;
}
.ml-chart-hero-label {
    font-size: 12px;
    color: #5b6575;
    letter-spacing: 0.3px;
}
.ml-chart-hero-val {
    font-size: 24px;
    font-weight: 700;
    color: #18212f;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
}
.ml-chart-hero-val small {
    font-size: 13px;
    font-weight: 500;
    color: #5b6575;
}
.ml-delta {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    white-space: nowrap;
}
.ml-delta.up {
    background: #fdecea;
    color: #b42318;
}
.ml-delta.down {
    background: #e7f6ee;
    color: #18794e;
}
.ml-delta.sm {
    font-size: 11px;
    padding: 1px 6px;
}
.ml-plot {
    position: relative;
    display: flex;
    height: 250px;
    margin-top: 18px;
}
.ml-yaxis {
    position: relative;
    width: 44px;
    flex: none;
}
.ml-yaxis span {
    position: absolute;
    right: 8px;
    transform: translateY(50%);
    font-size: 11px;
    color: #8a93a2;
    font-variant-numeric: tabular-nums;
}
.ml-plot-area {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
}
.ml-grid {
    position: absolute;
    left: 0;
    right: 0;
    border-top: 1px solid #eef1f5;
}
.ml-grid.base {
    border-top-color: #c9d0da;
}
.ml-avg {
    position: absolute;
    left: 0;
    right: 0;
    border-top: 1px dashed #7c8696;
    pointer-events: none;
}
.ml-cols {
    position: absolute;
    inset: 0;
    display: flex;
}
.ml-colb {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    height: 100%;
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    cursor: default;
    border-radius: 8px;
    outline: none;
}
/* แถบพื้นหลังจางๆ: รอบที่กำลังดู / ที่ชี้อยู่ */
.ml-colb::before {
    content: "";
    position: absolute;
    top: -6px;
    bottom: 0;
    left: 6%;
    right: 6%;
    border-radius: 8px;
    background: transparent;
    transition: background 0.15s;
}
.ml-colb.on::before {
    background: #eef3fd;
}
.ml-colb.hover::before {
    background: #f2f4f7;
}
.ml-colb.on.hover::before {
    background: #e3ebfb;
}
.ml-colb:focus-visible::before {
    box-shadow: inset 0 0 0 2px #93b4f5;
}
.ml-colb.clickable {
    cursor: pointer;
}
.ml-colb-stack {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 52%;
    max-width: 56px;
    display: flex;
    flex-direction: column-reverse;
    gap: 2px;
    border-radius: 4px 4px 0 0;
    overflow: hidden;
    transition: height 0.45s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.ml-seg {
    display: block;
    flex-basis: 0;
    min-height: 0;
    transition: opacity 0.15s;
}
.ml-seg.faded {
    opacity: 0.18;
}
.ml-colb:not(.on):not(.hover) .ml-colb-stack {
    opacity: 0.78;
}
.ml-colb-total {
    position: absolute;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #5b6575;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 0 3px #fff, 0 0 3px #fff;
    z-index: 1;
}
.ml-colb.on .ml-colb-total,
.ml-colb.hover .ml-colb-total {
    color: #18212f;
    font-size: 13px;
    font-weight: 700;
}
.ml-xaxis {
    display: flex;
    margin-left: 44px;
    padding-top: 8px;
}
.ml-xaxis span {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    font-size: 12px;
    color: #5b6575;
}
.ml-xaxis span.on {
    color: #1e3a8a;
    font-weight: 700;
}
.ml-xaxis span.hover {
    color: #18212f;
}
.ml-xaxis em {
    font-style: normal;
    font-size: 10px;
    font-weight: 600;
    color: #fff;
    background: #1e3a8a;
    padding: 0 6px;
    border-radius: 999px;
}
.ml-tip {
    position: absolute;
    top: 0;
    transform: translateX(14px);
    width: 240px;
    background: #18212f;
    color: #fff;
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.25);
    z-index: 5;
    pointer-events: none;
}
.ml-tip.flip {
    transform: translateX(calc(-100% - 14px));
}
.ml-tip-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 13px;
}
.ml-tip .ml-delta.up {
    background: rgba(248, 113, 113, 0.18);
    color: #fca5a5;
}
.ml-tip .ml-delta.down {
    background: rgba(74, 222, 128, 0.16);
    color: #86efac;
}
.ml-tip-total {
    font-size: 18px;
    font-weight: 700;
    margin: 2px 0 8px;
    font-variant-numeric: tabular-nums;
}
.ml-tip-total small {
    font-size: 11px;
    font-weight: 500;
    color: #c3cad6;
}
.ml-tip-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.ml-tip-list li {
    display: grid;
    grid-template-columns: 10px minmax(0, 1fr) auto 34px;
    align-items: center;
    gap: 6px;
}
.ml-tip-list i {
    width: 10px;
    height: 10px;
    border-radius: 3px;
}
.ml-tip-list .n {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #dfe4ec;
}
.ml-tip-list .v {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}
.ml-tip-list .p {
    text-align: right;
    color: #a9b2c0;
    font-variant-numeric: tabular-nums;
}
.ml-tip-foot {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    color: #bfd0ff;
    font-size: 11px;
}
.ml-zlegend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 14px;
    margin-left: 44px;
}
.ml-zlegend button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: 0;
    padding: 2px 0;
    font: inherit;
    font-size: 12px;
    color: #3a4555;
    cursor: default;
    transition: opacity 0.15s;
}
.ml-zlegend button.faded {
    opacity: 0.35;
}
.ml-zlegend-avg {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #5b6575;
    margin-left: auto;
}
.ml-zlegend-avg b {
    width: 18px;
    border-top: 1px dashed #7c8696;
}
.ml-zlegend i {
    width: 10px;
    height: 10px;
    border-radius: 3px;
}
.ml-top {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 4px 12px;
    font-size: 14px;
    align-items: center;
}
.ml-top-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.ml-top-val {
    font-weight: 600;
}
.ml-top .ml-bar {
    grid-column: 1 / -1;
    margin-bottom: 4px;
}
.ml-bar {
    height: 6px;
    background: #eef1f5;
    border-radius: 3px;
}
.ml-bar.w120 {
    width: 120px;
}
.ml-bar-fill {
    height: 6px;
    background: #1d4ed8;
    border-radius: 3px;
}
.ml-bar-fill.orange {
    background: #ea580c;
}

/* Table */
.ml-table-wrap {
    overflow-x: auto;
}
.ml-table {
    width: 100%;
    min-width: 900px;
    border-collapse: collapse;
    font-size: 14px;
}
.ml-table th,
.ml-table td {
    padding: 12px 8px;
    border-bottom: 1px solid #eef1f5;
    text-align: right;
}
.ml-table th {
    font-size: 13px;
    font-weight: 500;
    color: #5b6575;
    border-bottom-color: #e3e7ed;
}
.ml-table th button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    cursor: pointer;
    padding: 4px 0;
}
.ml-table .l {
    text-align: left;
}
.ml-table .b {
    font-weight: 600;
}
.ml-cell-bar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
}
.ml-hl {
    background: #fff1e6;
    color: #9a3412;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
}
.ml-pill {
    display: inline-block;
    font-size: 12px;
    padding: 2px 8px;
    margin: 0 4px 4px 0;
    border-radius: 999px;
    background: #f1f3f6;
    color: #3a4555;
    white-space: nowrap;
}
.ml-pill.ok {
    background: #e8f5ee;
    color: #166534;
}
.ml-pill.warn {
    background: #fff1e6;
    color: #9a3412;
}
.ml-pill.wait {
    background: #fff7d6;
    color: #854d0e;
}
.ml-pill.bad {
    background: #fde8e8;
    color: #991b1b;
}

/* ปุ่มเปิดโซน */
.ml-zone-btn {
    position: relative;
    display: inline-flex;
    align-items: stretch;
    height: 38px;
    border-radius: 10px;
    background: #1e3a8a;
    color: #fff !important;
    text-decoration: none;
    border: 0;
    padding: 0;
    font: inherit;
    cursor: pointer;
    box-shadow: 0 1px 0 #0f2463, 0 2px 6px rgba(30, 58, 138, 0.18);
    transition: transform 0.1s, box-shadow 0.1s;
}
.ml-zone-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 0 #0f2463, 0 4px 10px rgba(30, 58, 138, 0.25);
}
.ml-zone-btn:focus-visible {
    outline: 3px solid #93b4f5;
    outline-offset: 2px;
}
/* ดูรอบเก่า: สีเทาเข้มให้ตรงกับแบนเนอร์รอบที่ปิดแล้ว */
.ml-zone-btn.past {
    background: #334155;
}
.ml-zone-btn.past:hover {
    box-shadow: 0 2px 0 #1e293b, 0 4px 10px rgba(51, 65, 85, 0.25);
}
.ml-zone-btn-main {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 10px 0 12px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
}
.ml-zone-btn-go {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    margin: 4px 4px 4px 0;
    border-radius: 7px;
    background: #fde68a;
}
.ml-zone-btn-dot {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    box-sizing: border-box;
    border-radius: 9px;
    background: #ea580c;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #fff;
}

</style>
